import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, MessageSquareText } from "lucide-react";
import { EmptyState } from "@/components/common/EmptyState";
import { EntryModal } from "@/components/common/EntryModal";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { useUser } from "@/context/UserContext";

const fmt = (iso) => {
  try { return new Date(iso).toLocaleString(undefined, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }); }
  catch { return "—"; }
};

export const UpdatesHistory = ({ relatedId, relatedType, relatedName, area = "Product", onChange, title = "Update History" }) => {
  const { currentUser, canWrite } = useUser();
  const [open, setOpen] = useState(false);
  const [updates, setUpdates] = useState([]);

  const load = useCallback(async () => {
    try { setUpdates(await api.updatesFor(relatedId)); } catch { setUpdates([]); }
  }, [relatedId]);
  useEffect(() => { if (relatedId) load(); }, [relatedId, load]);

  const handleSave = async (values) => {
    await api.create("updates", {
      ...values,
      author: currentUser.name,
      area,
      related_type: relatedType,
      related_name: relatedName,
      related_id: relatedId,
    });
    toast.success("Update posted");
    await load();
    onChange?.();
  };

  return (
    <div data-testid="updates-history">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="font-heading text-lg font-semibold text-[#14245F]">{title}</h3>
        {canWrite && (
          <Button size="sm" data-testid="add-record-update-btn" onClick={() => setOpen(true)} className="rounded-full bg-[#0EA5A4] text-white hover:bg-[#0C8E8D]">
            <Plus size={15} /> Add Update
          </Button>
        )}
      </div>
      {updates.length === 0 ? (
        <EmptyState icon={MessageSquareText} title="No updates yet" description={`Post the first update for this ${relatedType?.toLowerCase() || "record"}.`} actionLabel={canWrite ? "Add update" : undefined} onAction={canWrite ? () => setOpen(true) : undefined} />
      ) : (
        <ul className="space-y-3">
          {updates.map((u) => (
            <li key={u.id} className="rounded-2xl border border-[#E4E9F3] bg-white p-4">
              <div className="mb-1 flex items-center justify-between gap-2">
                <p className="font-semibold text-[#14245F]">{u.title}</p>
                <span className="shrink-0 text-xs text-[#5A6B8C]">{u.author} · {fmt(u.created_at)}</span>
              </div>
              <p className="whitespace-pre-wrap text-sm text-[#5A6B8C]">{u.body}</p>
            </li>
          ))}
        </ul>
      )}

      <EntryModal
        open={open}
        onOpenChange={setOpen}
        title="Post an Update"
        description={`Posting as ${currentUser.name} · ${relatedType}: ${relatedName}`}
        submitLabel="Post Update"
        fields={[
          { name: "title", label: "Title", type: "text", placeholder: "Short summary", required: true },
          { name: "body", label: "Update", type: "textarea", placeholder: "What's the latest?", required: true },
        ]}
        onSubmit={handleSave}
      />
    </div>
  );
};
